import { TEMPLATE_LOCK_ATTR, USER_LOCK_ATTR } from '@/entities/section-picker';
import { SELECTION_HELPER_ATTR } from './applyElementConfig';

export interface CustomAttribute {
  name: string;
  value: string;
}

/**
 * Attributes this app writes and reads as state. Editing them by hand would corrupt that state.
 *
 * Built from the canonical constants rather than re-typed: renaming a lock marker without this
 * list following would silently surface a template lock as a user-editable, deletable row.
 */
const INTERNAL_ATTRIBUTES = new Set([SELECTION_HELPER_ATTR, USER_LOCK_ATTR, TEMPLATE_LOCK_ATTR]);

/**
 * Attributes that carry the element's identity or rendering rather than extra metadata. They have
 * dedicated affordances (the image source picker sets `src`), and a typo in one silently breaks the
 * template, so the custom-attribute editor stays out of them.
 */
// eslint-disable-next-line i18next/no-literal-string -- HTML attribute names, not copy
const STRUCTURAL_ATTRIBUTES = new Set(['src', 'href', 'class', 'style', 'id']);

/**
 * Inline event handlers. This HTML is published to a sending platform and rendered directly in the
 * preview iframe, so an `onclick` here is an injection vector rather than a feature. Anchored to
 * letters-only so a legitimate hyphenated name like `on-brand-color` still passes.
 */
const EVENT_HANDLER_PATTERN = /^on[a-z]+$/i;

/** HTML attribute-name grammar, minus the characters that would need escaping to write safely. */
const ATTRIBUTE_NAME_PATTERN = /^[A-Za-z_:][\w.:-]*$/;

export function isReservedAttributeName(name: string): boolean {
  const normalized = name.trim().toLowerCase();
  if (!normalized) return true;
  if (normalized.startsWith('data-updatable-')) return true;
  if (EVENT_HANDLER_PATTERN.test(normalized)) return true;
  return INTERNAL_ATTRIBUTES.has(normalized) || STRUCTURAL_ATTRIBUTES.has(normalized);
}

export function isValidAttributeName(name: string): boolean {
  return ATTRIBUTE_NAME_PATTERN.test(name.trim());
}

/**
 * Narrow a raw attribute list (see `getElementSnapshot`) down to the ones a user may edit.
 *
 * Nothing records which attributes were user-added, so "custom" is defined by exclusion rather than
 * by bookkeeping: everything that is not reserved is offered for editing. That keeps a template
 * author able to fix an attribute they added in a previous session without us having to persist a
 * second source of truth alongside the HTML.
 */
export function toCustomAttributes(
  attributes: ReadonlyArray<{ name: string; value: string }>,
): CustomAttribute[] {
  return attributes
    .filter((attribute) => !isReservedAttributeName(attribute.name))
    .map(({ name, value }) => ({ name, value }));
}
